import React, { useEffect, useState } from "react";
import { Users, HeartPulse, Activity, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { HospitalHeader, BrandedShell, StatChip, BrandedNav } from "@/components/BrandedLayout";
import { FooterDisclaimer } from "@/components/FooterDisclaimer";
import { useBranding } from "@/lib/branding";
import { riskLevel, RISK_STYLES } from "@/lib/appUtils";
import { cn } from "@/lib/utils";

export default function HospitalAdmin() {
  const { hospCode } = useBranding();
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    api.listDoctors(hospCode).then(setDoctors);
    api.listPatients(hospCode).then(setPatients);
  }, [hospCode]);

  const avgRisk = patients.length ? Math.round(patients.reduce((a, p) => a + p.risk_score, 0) / patients.length) : 0;

  return (
    <BrandedShell>
      <HospitalHeader subtitle="Hospital administration" testId="admin-header" />
      <BrandedNav role="doctor" />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <StatChip icon={Users} label="Doctors" value={doctors.length} testId="admin-stat-doctors" accent />
        <StatChip icon={HeartPulse} label="Active Patients" value={patients.length} testId="admin-stat-patients" accent />
        <StatChip icon={Activity} label="Avg Risk Score" value={avgRisk} testId="admin-stat-avg-risk" />
        <StatChip icon={Stethoscope} label="High Risk" value={patients.filter((p) => p.risk_score > 60).length} testId="admin-stat-high-risk" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card p-6 shadow-sm">
          <div className="font-semibold mb-4">Care team</div>
          <ul className="divide-y divide-border">
            {doctors.map((d) => (
              <li key={d.id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border border-border bg-secondary text-foreground flex items-center justify-center font-semibold text-xs">
                  {d.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-medium text-sm">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.specialty_label}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="surface-card p-6 shadow-sm">
          <div className="font-semibold mb-4">Patient risk distribution</div>
          <ul className="space-y-2">
            {patients.map((p) => {
              const rl = riskLevel(p.risk_score);
              const style = RISK_STYLES[rl];
              return (
                <li key={p.id} className="flex items-center gap-3">
                  <span className="text-xs w-32 truncate">{p.name}</span>
                  <div className="flex-1 h-1 rounded-full bg-secondary overflow-hidden">
                    <div className={cn("h-full", style.bar)} style={{ width: `${p.risk_score}%` }} />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground w-8 text-right tabular-nums">{p.risk_score}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <FooterDisclaimer />
    </BrandedShell>
  );
}
